/**
 * Project methodology helpers.
 *
 * A project's `project_type` decides the shape of its `methodology_config`,
 * and the backend validator (`services/projects_methodology.py`) rejects a
 * mismatch with a 422. The three shapes are:
 *
 *   waterfall     -> { phases: [{ name }, ...] }            (non-empty)
 *   agile         -> { columns: [{ id, name, order, is_done? }, ...] }
 *                    (non-empty, unique ids, at most one is_done)
 *   waves_threads -> { wave_count_target: int >= 1, modules: [str] }
 *                    (non-empty, unique module names)
 *
 * Extracted from the component so the shaping rules are unit-testable
 * without mounting a modal — a 422 on project create is otherwise only
 * discoverable by clicking through the Assign Contract flow.
 */

export const PROJECT_TYPES = ['waterfall', 'agile', 'waves_threads'];

export const METHODOLOGY_LABELS = {
  waterfall: 'Waterfall',
  agile: 'Agile',
  waves_threads: 'Waves & Threads',
};

export const METHODOLOGY_HINTS = {
  waterfall: 'Sequential phases, each finishing before the next begins.',
  agile: 'A board of columns; work flows left to right.',
  waves_threads: 'Coordinated waves across module threads.',
};

/** Split a comma-separated field into trimmed, de-duplicated, non-empty names. */
export function parseNameList(raw) {
  const seen = new Set();
  return String(raw || '')
    .split(',')
    .map((s) => s.trim())
    .filter((s) => {
      if (!s || seen.has(s)) return false;
      seen.add(s);
      return true;
    });
}

/** Stable, url-safe column id derived from its name. Backend requires unique ids. */
export function columnId(name, index) {
  const slug = String(name || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return slug || `col_${index + 1}`;
}

/**
 * Build a valid `methodology_config` for the given type.
 *
 * Returns `{ config }` on success or `{ error }` with a message suitable for a
 * toast. Never throws — the caller is a click handler.
 *
 * The last agile column is marked `is_done`, matching how every board in this
 * codebase is seeded. The backend tolerates several `is_done` columns (last
 * wins) but we only ever send one.
 */
export function buildMethodologyConfig(projectType, fields = {}) {
  if (!PROJECT_TYPES.includes(projectType)) {
    return { error: `Unknown methodology '${projectType}'` };
  }

  if (projectType === 'waterfall') {
    const phases = parseNameList(fields.phases);
    if (phases.length === 0) {
      return { error: 'Waterfall projects need at least one phase.' };
    }
    return { config: { phases: phases.map((name) => ({ name })) } };
  }

  if (projectType === 'agile') {
    const names = parseNameList(fields.columns);
    if (names.length === 0) {
      return { error: 'Agile projects need at least one board column.' };
    }
    const ids = new Set();
    const columns = names.map((name, i) => {
      let id = columnId(name, i);
      // columnId can collide when two names slugify identically ("To Do" /
      // "to-do"). parseNameList only de-dupes the raw strings.
      while (ids.has(id)) id = `${id}_${i + 1}`;
      ids.add(id);
      const col = { id, name, order: i };
      if (i === names.length - 1) col.is_done = true;
      return col;
    });
    return { config: { columns } };
  }

  // waves_threads
  const waveCount = Number(fields.waveCountTarget);
  if (!Number.isInteger(waveCount) || waveCount < 1) {
    return { error: 'Wave count must be a whole number of 1 or more.' };
  }
  const modules = parseNameList(fields.modules);
  if (modules.length === 0) {
    return { error: 'Waves & Threads projects need at least one module.' };
  }
  return { config: { wave_count_target: waveCount, modules } };
}

/** Defaults that produce a valid config for each type with no user input. */
export const METHODOLOGY_DEFAULTS = {
  phases: 'Delivery',
  columns: 'To Do, In Progress, Done',
  waveCountTarget: 3,
  modules: 'Core',
};
